import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook to debounce a value.
 *
 * Delays updating the returned debouncedValue until after delay milliseconds
 * have elapsed since the last time value changed.
 *
 * @template T - The type of the value to debounce
 * @param {T} value - The value to debounce
 * @param {number} [delay=300] - The delay in milliseconds (default: 300ms, industry standard)
 * @returns {T} The debounced value
 *
 * @example
 * ```tsx
 * const [searchTerm, setSearchTerm] = useState('');
 * const debouncedSearchTerm = useDebounce(searchTerm, 500);
 *
 * // Effect only runs when debounced value changes (after user stops typing)
 * useEffect(() => {
 *   if (debouncedSearchTerm) {
 *     performSearch(debouncedSearchTerm);
 *   }
 * }, [debouncedSearchTerm]);
 *
 * return (
 *   <input
 *     value={searchTerm}
 *     onChange={(e) => setSearchTerm(e.target.value)}
 *     // Input updates immediately, but search uses debounced value
 *   />
 * );
 * ```
 */
export function useDebounce<T>(value: T, delay: number = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    // Set up timer to update debounced value after delay
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Clean up timer if value changes before delay expires
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Custom hook to create a debounced callback function.
 *
 * Returns a debounced version of the callback that delays invoking
 * until after wait milliseconds have elapsed since the last invocation.
 *
 * @template T - The function type to debounce
 * @param {T} callback - The function to debounce
 * @param {number} [delay=300] - The delay in milliseconds
 * @returns {T} A debounced version of the callback
 *
 * @example
 * ```tsx
 * const debouncedValidate = useDebouncedCallback(
 *   (value: string) => validateEmail(value),
 *   500
 * );
 *
 * <input
 *   onChange={(e) => {
 *     setValue(e.target.value);
 *     debouncedValidate(e.target.value);
 *   }}
 * />
 * ```
 */
export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number = 300
): T {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const debouncedFn = useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay]
  ) as T;

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedFn;
}
